import * as THREE from "three";
import {
  threeGeomInstanceFragShader,
  threeGeomInstanceVertShader,
} from "../shaders/threeGeomInstanceShader.js";
import mapboxgl from "mapbox-gl";
import {getScalingFactor} from "../helpers.js";
const plateSize = 1_000_000;
const earthRadius = 1303.797293808806;
import Stats from "three/examples/jsm/libs/stats.module.js";
import {threeGeomFixedFragShader, threeGeomFixedVertShader} from "../shaders/threeGeomFixedShader.js";

const anchor = [0, 0];

let statsPanels;

export const fixedThreeGeomLayer = {
  id: 'fixed-three-geom-layer', type: 'custom', renderingMode: '3d',

  onAdd(map, gl) {

    this.map = window.map = map;

    const stats1 = new Stats();
    stats1.showPanel(0);
    document.body.appendChild(stats1.dom);

    const stats2 = new Stats();
    stats2.showPanel(1);
    stats2.dom.style.left = '80px';
    document.body.appendChild(stats2.dom);

    const stats3 = new Stats();
    stats3.showPanel(2);
    stats3.dom.style.left = '160px';
    document.body.appendChild(stats3.dom);
    statsPanels = [stats1, stats2, stats3];


    this.camera = new THREE.Camera();
    this.scene = new THREE.Scene();

    this.size = 0.001;
    this.blipsx = 10;
    this.blipsy = 10;
    this.space = 800;

    const box = new THREE.BoxGeometry(this.size, this.size, this.size);

    box.applyMatrix4(new THREE.Matrix4().makeTranslation(0, 0, this.size / 2));

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        'u_matrix_globe_merc': {value: new THREE.Matrix4()},
        'u_plate_tl_up': {value: new THREE.Vector3()},
        'u_plate_tr_up': {value: new THREE.Vector3()},
        'u_plate_br_up': {value: new THREE.Vector3()},
        'u_plate_bl_up': {value: new THREE.Vector3()},
        'u_plate_tl': {value: new THREE.Vector3()},
        'u_plate_tr': {value: new THREE.Vector3()},
        'u_plate_br': {value: new THREE.Vector3()},
        'u_plate_bl': {value: new THREE.Vector3()},
        'u_plate_height': {value: 0},
        'u_plate_width': {value: 0},
        'u_plate_up_scale': {value: 0},
        'u_zoom_transition': {value: 0},
        'u_centerInMerc': {value: new THREE.Vector2()},
        'u_pixelsPerMeterRatio': {value: 0},
        'u_scale_factor': {value: 0},
        'u_colour': {value: new THREE.Vector3(Math.random(), Math.random(), Math.random())},
        'u_map_zoom': { value: 0 }
      },
      vertexShader: threeGeomFixedVertShader,
      fragmentShader: threeGeomFixedFragShader,
      depthTest: true,
      depthWrite: true,
      depthFunc: THREE.LessEqualDepth
    });

    this.mesh = new THREE.InstancedMesh(box, this.material, this.blipsx * this.blipsy);

    this.mesh.frustumCulled = false;    //hmm!

    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    this.models = [];

    let i = 0;

    for (let x = 0; x < this.blipsx; x++) {

      for (let y = 0; y < this.blipsy; y++) {

        const model = {
          x: (anchor[0]) + (x * this.size * this.space), y: (anchor[1]) + (y * this.size * this.space)
        };

        this.models.push(model);

        const mc = mapboxgl.MercatorCoordinate.fromLngLat([model.x, model.y], 0);

        const scale = mc.meterInMercatorCoordinateUnits() * 100;

        const matrix = new THREE.Matrix4()
          .makeRotationAxis(new THREE.Vector3(0, 0, 1), Math.random() * Math.PI)
          .premultiply(new THREE.Matrix4().makeTranslation(mc.x, mc.y, mc.z));

        this.mesh.setMatrixAt(i, matrix);

        this.mesh.setColorAt(i, new THREE.Color(Math.random(), Math.random(), Math.random()));

        i++;

      }

    }

    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor.needsUpdate = true;

    this.mesh.computeBoundingBox();
    this.mesh.computeBoundingSphere();

    this.scene.add(this.mesh);

    this.renderer = new THREE.WebGLRenderer({
      canvas: map.getCanvas(), context: gl, antialias: true
    });

    this.renderer.autoClear = false;

    this.renderer.render(this.scene, this.camera);

  },

  render(gl, projectionMatrix, projection, globeToMercMatrix, transition, centerInMercator, pixelsPerMeterRatio) {
    const zoom = this.map.getZoom();



    statsPanels.forEach(stats => stats.begin());
    const u = this.material.uniforms;


    if (projection && projection.name === 'globe') {

      if (!this.plate) {

        const plate = new mapboxgl.LngLat(anchor[0], anchor[1]).toBounds(plateSize);

        const nw_merc = mapboxgl.MercatorCoordinate.fromLngLat(plate.getNorthWest(), 0);
        const sw_merc = mapboxgl.MercatorCoordinate.fromLngLat(plate.getSouthWest(), 0);
        const ne_merc = mapboxgl.MercatorCoordinate.fromLngLat(plate.getNorthEast(), 0);
        const se_merc = mapboxgl.MercatorCoordinate.fromLngLat(plate.getSouthEast(), 0);

        u.u_plate_tl_up.value.fromArray(plate.getNorthWest().toEcef(0));
        u.u_plate_br_up.value.fromArray(plate.getSouthWest().toEcef(0));
        u.u_plate_tr_up.value.fromArray(plate.getNorthEast().toEcef(0));
        u.u_plate_bl_up.value.fromArray(plate.getSouthEast().toEcef(0));

        u.u_plate_tl.value.set(nw_merc.x, nw_merc.y, nw_merc.z);
        u.u_plate_bl.value.set(sw_merc.x, sw_merc.y, sw_merc.z);
        u.u_plate_tr.value.set(ne_merc.x, ne_merc.y, ne_merc.z);
        u.u_plate_br.value.set(se_merc.x, se_merc.y, se_merc.z);
        u.u_plate_width.value = nw_merc.x - ne_merc.x;
        u.u_plate_height.value = sw_merc.y - nw_merc.y;

        u.u_scale_factor.value = getScalingFactor(anchor[1]);
        u.u_plate_up_scale.value = earthRadius;

        this.plate = true;

      }

      u.u_zoom_transition.value = transition;
      u.u_pixelsPerMeterRatio.value = pixelsPerMeterRatio;
      u.u_matrix_globe_merc.value.fromArray(globeToMercMatrix);
      u.u_centerInMerc.value.fromArray(centerInMercator);


      if (zoom < 4.5) {
        // hack to test what scaling works at weird zooms
        const alter = Math.min(4.5 - zoom, 1);
        u.u_map_zoom.value = Math.min(zoom + alter, 12);
      } else {
        u.u_map_zoom.value = Math.min(zoom, 12);
      }
    } else {
      u.u_map_zoom.value = Math.min(zoom, 12);
      u.u_zoom_transition.value = 1;
    }


    console.log("zoom", zoom);
    this.renderer.clearDepth();

    this.camera.projectionMatrix.fromArray(projectionMatrix);   //[Merc to World] [World to Camera] [Camera to Clip]

    this.renderer.resetState();
    this.renderer.render(this.scene, this.camera);
    statsPanels.forEach(stats => stats.end());
  }

}
